/* @flow */
import {
  getBraindateConversation,
  getBraindatePod,
} from '@e180/domain/lib/braindate/util';
import {
  getEventBraindatePods,
  getEventBraindateMeetingPoint,
  getEventGroupBraindatePods,
  getEventGroupBraindateMeetingPoint,
} from '@e180/domain/lib/event/util';
import { assertObject, assertString } from '@e180/util/lib/assert';
import { isNumber } from '@e180/util/lib/type';

import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';

import type { BraindateEvent } from '@e180/domain/lib/event/type';
import type { Braindate } from '@e180/domain/lib/braindate/type';

/**
 * Get the name of the pod for a given braindate
 * @param  {Braindate} braindate - Braindate to look into for the pod number
 * @param  {BraindateEvent} event - Event contains the pod names
 * @param  {string} locale - Which locale to get the pod in

 * @return {string} - The pod name for the braindate
 */
export function getBraindatePodName(
  braindate: Braindate,
  event: BraindateEvent,
  locale: string,
): string {
  assertObject(braindate, 'braindate');
  assertObject(event, 'event');
  assertString(locale, 'string');

  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);
  const pod = getBraindatePod(braindate);

  const podNames =
    (isGroupBraindate
      ? getEventGroupBraindatePods(event, locale)
      : getEventBraindatePods(event, locale)) || [];

  // $FlowIssue - We have ascertain pod is a number
  return isNumber(pod) ? podNames[pod - 1] || String(pod) : pod;
}

/**
 * Get the name of the meetingPoint for a given braindate
 * @param  {Braindate} braindate - Braindate to determine if it's a one-on-one or group
 * @param  {BraindateEvent} event - Event contains the meeting point names
 * @param  {string} locale - Which locale to get the meeting point in

 * @return {string} - The meeting point name for the braindate
 */
export function getBraindateMeetingPoint(
  braindate: Braindate,
  event: BraindateEvent,
  locale: string,
): string {
  assertObject(braindate, 'braindate');
  assertObject(event, 'event');
  assertString(locale, 'string');

  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);

  return isGroupBraindate
    ? getEventGroupBraindateMeetingPoint(event, locale)
    : getEventBraindateMeetingPoint(event, locale);
}
